import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import {
  MlaAddressHistory,
  MlaBorrowerInfo,
  MlaBorrowerInfoResponse,
  MlaBorrowerUpdateRequest,
  MlaEmployment,
  MlaIncomeSource,
} from '@ld.consumer-portal/models';
import { MlaAuthService } from './mla-auth.service';
import { LoggingService, ThemeService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class MlaBorrowerService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);
  private readonly themeService = inject(ThemeService);

  private readonly cachedPrimaryBorrower = signal<MlaBorrowerInfo | null>(null);
  private readonly cachedBorrowers = signal<MlaBorrowerInfo[]>([]);

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  private readonly DEFAULT_BORROWER_FIELDS = [
    'borrowerInfoId',
    'firstName',
    'lastName',
    'email',
    'phone',
    'ssnVerify',
    'isPrimary',
    'borrowerToken',
    'loanDetail',
  ];

  async getBorrowerInfo(fields?: string[], getMostRecentAddress = false): Promise<MlaBorrowerInfo[]> {
    const sessionValid = await this.authService.ensureMlaSession();
    if (!sessionValid) {
      this.loggingService.logWarning('[MlaBorrowerService] Failed to establish MLA session');
      return [];
    }

    const fieldList = fields?.join(',') || this.DEFAULT_BORROWER_FIELDS.join(',');
    const params = new HttpParams()
      .set('fields', fieldList)
      .set('getMostRecentAddress', getMostRecentAddress.toString());

    const url = `${this.mlaApiUrl}api/v1/borrower-info`;

    try {
      const response = await firstValueFrom(this.http.get<MlaBorrowerInfoResponse>(url, { params }));

      if (response?.success && response.data) {
        const primary = response.data.find((b) => b.isPrimary) || response.data.find((b) => !!b.borrowerToken);
        if (primary?.borrowerToken) {
          this.authService.setDashboardToken(primary.borrowerToken);
        }

        this.cachedPrimaryBorrower.set(response.data.find((b) => b.isPrimary) || response.data[0] || null);
        this.cachedBorrowers.set(response.data);

        return response.data;
      }

      return [];
    } catch (error) {
      this.loggingService.logException(error, 'MlaBorrowerService: Error fetching borrower info');
      return [];
    }
  }

  getBorrowerInfo$(fields?: string[], getMostRecentAddress = false): Observable<MlaBorrowerInfo[]> {
    return from(this.authService.ensureMlaSession()).pipe(
      switchMap((sessionValid) => {
        if (!sessionValid) {
          this.loggingService.logWarning('[MlaBorrowerService] Failed to establish MLA session');
          return of([]);
        }

        const fieldList = fields?.join(',') || this.DEFAULT_BORROWER_FIELDS.join(',');
        const params = new HttpParams()
          .set('fields', fieldList)
          .set('getMostRecentAddress', getMostRecentAddress.toString());

        const url = `${this.mlaApiUrl}api/v1/borrower-info`;

        return this.http.get<MlaBorrowerInfoResponse>(url, { params }).pipe(
          map((response) => response?.data || []),
          tap((data) => {
            if (data.length) {
              const primary = data.find((b) => b.isPrimary) || data.find((b) => !!b.borrowerToken);
              if (primary?.borrowerToken) {
                this.authService.setDashboardToken(primary.borrowerToken);
              }
              this.cachedPrimaryBorrower.set(data.find((b) => b.isPrimary) || data[0] || null);
              this.cachedBorrowers.set(data);
            }
          }),
        );
      }),
      catchError((error) => {
        this.loggingService.logException(error, 'MlaBorrowerService: Error fetching borrower info');
        return of([]);
      }),
    );
  }

  async getPrimaryBorrower(): Promise<MlaBorrowerInfo | null> {
    const borrowers = await this.getBorrowerInfo();
    return borrowers.find((b) => b.isPrimary) || borrowers.find((b) => !!b.borrowerToken) || borrowers[0] || null;
  }

  async getCoBorrower(): Promise<MlaBorrowerInfo | null> {
    const borrowers = await this.getBorrowerInfo();
    return borrowers.find((b) => !b.isPrimary) || null;
  }

  async getBorrowerToken(): Promise<string | null> {
    const primary = await this.getPrimaryBorrower();
    return primary?.borrowerToken || this.authService.dashboardToken;
  }

  async updateBorrower(borrowerId: string, updates: MlaBorrowerUpdateRequest): Promise<boolean> {
    const url = `${this.mlaApiUrl}api/v1/borrower/${borrowerId}`;

    try {
      const response = await firstValueFrom(this.http.patch<{ success: boolean }>(url, updates));

      if (response?.success) {
        this.clearCache();
        return true;
      }

      return false;
    } catch (error) {
      this.loggingService.logException(error, 'MlaBorrowerService: Error updating borrower');
      return false;
    }
  }

  async getAddressHistory(borrowerId: string): Promise<MlaAddressHistory[]> {
    const params = new HttpParams().set('borrowerId', borrowerId);
    const url = `${this.mlaApiUrl}api/v1/address-history`;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: MlaAddressHistory[] }>(url, { params }),
      );

      return response?.data || [];
    } catch (error) {
      this.loggingService.logException(error, 'MlaBorrowerService: Error fetching address history');
      return [];
    }
  }

  async getEmployment(loanId: string, borrowerId: string): Promise<MlaEmployment[]> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}/borrower/${borrowerId}/employment`;

    try {
      const response = await firstValueFrom(this.http.get<{ success: boolean; data: MlaEmployment[] }>(url));

      return response?.data || [];
    } catch (error) {
      this.loggingService.logException(error, 'MlaBorrowerService: Error fetching employment');
      return [];
    }
  }

  async getIncomeSources(borrowerId: string): Promise<MlaIncomeSource[]> {
    const params = new HttpParams().set('borrowerId', borrowerId);
    const url = `${this.mlaApiUrl}api/v1/income`;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: MlaIncomeSource[] }>(url, { params }),
      );

      return response?.data || [];
    } catch (error) {
      this.loggingService.logException(error, 'MlaBorrowerService: Error fetching income sources');
      return [];
    }
  }

  getCachedPrimaryBorrower(): MlaBorrowerInfo | null {
    return this.cachedPrimaryBorrower();
  }

  clearCache(): void {
    this.cachedPrimaryBorrower.set(null);
    this.cachedBorrowers.set([]);
  }

  getCachedBorrowers(): MlaBorrowerInfo[] {
    return this.cachedBorrowers();
  }

  async getBorrowerInfoWithToken(
    b2cIdToken: string,
    fields?: string[],
    getMostRecentAddress = false,
  ): Promise<MlaBorrowerInfo[]> {
    if (!b2cIdToken) {
      this.loggingService.logWarning('[MlaBorrowerService] No B2C token provided for getBorrowerInfoWithToken');
      return [];
    }

    const fieldList = fields?.join(',') || this.DEFAULT_BORROWER_FIELDS.join(',');
    const params = new HttpParams()
      .set('fields', fieldList)
      .set('getMostRecentAddress', getMostRecentAddress.toString());

    const url = `${this.mlaApiUrl}api/v1/borrower-info`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: `Bearer ${b2cIdToken}`,
      b2ctoken: b2cIdToken,
      'authorization-provider': this.env.appSettings.useCustomerIdentityAuth ? 'Entra' : 'CP2',
      ...(this.env.appSettings.useCustomerIdentityAuth ? { 'X-Partner-Id': this.themeService.getXPartnerId() } : {}),
    });

    try {
      const response = await firstValueFrom(this.http.get<MlaBorrowerInfoResponse>(url, { params, headers }));

      if (response?.success && response.data) {
        this.authService.storeBorrowersFromBorrowerInfo(response.data);

        const primary = response.data.find((b) => b.isPrimary) || response.data.find((b) => !!b.borrowerToken);
        if (primary?.borrowerToken) {
          this.authService.setDashboardToken(primary.borrowerToken);
        }

        this.cachedPrimaryBorrower.set(response.data.find((b) => b.isPrimary) || response.data[0] || null);
        this.cachedBorrowers.set(response.data);

        return response.data;
      }

      return [];
    } catch (error) {
      this.loggingService.logException(error, '[MlaBorrowerService] Error fetching borrower info with token:');
      return [];
    }
  }
}
